import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

export interface Contrato {
  id: string;
  codigoTipo: string | null;
  status: 'RASCUNHO' | 'EMITIDO' | 'ASSINADO' | 'CANCELADO' | 'SUBSTITUIDO';
  associadoId: string;
  carroId: string;
  pdfHash: string | null;
  valorTotal: number | null;
  geradoEm: string;
  assinadoEm: string | null;
  ipAssinatura: string | null;
  motivoCancelamento: string | null;
}

/** Linha do acompanhamento. ⚠️ Sem caminho de arquivo nem token: o backend não os devolve. */
export interface ContratoLista {
  id: string;
  codigoTipo: string | null;
  titulo: string | null;
  status: Contrato['status'];
  associado: string | null;
  cpfCnpj: string | null;
  placa: string | null;
  modelo: string | null;
  valorTotal: number | null;
  geradoEm: string;
  assinadoEm: string | null;
  temPdf: boolean;
  motivoCancelamento: string | null;
}

export interface ListaContratos {
  total: number;
  pagina: number;
  limite: number;
  contratos: ContratoLista[];
  /** Contadores GLOBAIS — não seguem busca nem filtro (convenção at-10/at-28). */
  totais: { emitidos: number; assinados: number; cancelados: number };
}

export const listarContratos = async (params: {
  pagina?: number;
  limite?: number;
  busca?: string;
  status?: string;
  /** at-81: contratos de um veículo/associado — os termos da adesão em curso. */
  carroId?: string;
  associadoId?: string;
}): Promise<ListaContratos> => {
  try {
    const { data } = await http.get('/contratos', { params });
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao listar os contratos'));
  }
};

export const buscarContrato = async (id: string): Promise<Contrato> => {
  try {
    const { data } = await http.get(`/contratos/${id}`);
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao buscar o contrato'));
  }
};

/**
 * Baixa o PDF do contrato.
 *
 * ⚠️ O arquivo é **privado** desde a at-40: não existe mais URL pública para abrir
 * direto. A leitura passa pelo proxy autenticado, e por isso vem como Blob pelo
 * cliente `http` (que injeta o Bearer) — mesmo padrão da prévia de template.
 */
export const baixarPdfContrato = async (id: string): Promise<Blob> => {
  try {
    const { data } = await http.get(`/contratos/${id}/pdf`, { responseType: 'blob' });
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao abrir o PDF do contrato'));
  }
};

/**
 * Abre o PDF do contrato numa aba nova, buscando com sessão e liberando a URL
 * depois.
 *
 * 🔴 **A aba é aberta ANTES do download, de forma síncrona** (at-81 — feedback de
 * campo de 03/11/2026: *"gera o termo e não aparece o termo"*). Navegador de
 * celular só permite `window.open` dentro do gesto do usuário; abrir **depois** do
 * `await` do download é bloqueado em silêncio. Por isso esta função precisa ser
 * chamada **direto no `onClick`**, sem nenhum `await` antes dela.
 *
 * Quando nem assim a aba abre (bloqueio configurado), o PDF é **baixado** — o
 * consultor sempre recebe o documento. Devolve `true` se abriu em aba e `false`
 * se caiu no download; quem chama decide se avisa.
 *
 * ⚠️ O `revokeObjectURL` de 60s fica: o Chrome precisa da URL viva enquanto a aba
 * nova carrega o arquivo.
 */
export const abrirPdfContrato = async (id: string): Promise<boolean> => {
  // Síncrono, dentro do gesto — nada de `await` antes desta linha.
  const aba = window.open('', '_blank');
  if (aba) {
    try {
      aba.document.title = 'Carregando contrato…';
      aba.document.body.innerText = 'Carregando contrato…';
    } catch {
      // Algumas configurações não deixam escrever na aba: ela só fica em branco.
    }
  }

  let blob: Blob;
  try {
    blob = await baixarPdfContrato(id);
  } catch (erro) {
    aba?.close();
    throw erro;
  }

  const url = URL.createObjectURL(blob);
  setTimeout(() => URL.revokeObjectURL(url), 60000);

  if (aba) {
    aba.location.href = url;
    return true;
  }

  const link = document.createElement('a');
  link.href = url;
  link.download = `contrato-${id}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  return false;
};

export const assinarContrato = async (
  contratoId: string,
  assinaturaBase64: string,
): Promise<Contrato> => {
  try {
    const { data } = await http.post('/assinatura/contrato', {
      contratoId,
      assinaturaBase64,
    });
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao assinar o contrato'));
  }
};

export const cancelarContrato = async (id: string, motivo: string): Promise<Contrato> => {
  try {
    const { data } = await http.patch(`/contratos/${id}/cancelar`, { motivo });
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao cancelar o contrato'));
  }
};
